// Megbeszélés résztvevő-áttekintés + saját RSVP. HOL: a naptár RÉSZLET-KÁRTYÁJÁBAN (`MeetingPeekCard`) —
// a szerkesztő-sheet alsó szekciója volt, de ott a multiselect már megmutatta a kiválasztottakat,
// és a panel csak hosszabbá tette a formot (user-döntés). A résztvevőket
// avatar + név + RSVP-státusz-badge (Ott lesz / Nem megy / Talán) alakban listázza — a SZEREP-badge
// (munkatárs/kapcsolattartó) KIVEZETVE (user-döntés): a belsős/külsős megkülönböztetés a
// névből és a kontextusból amúgy is ismert, a soron csak zaj volt. EGY badge marad, és az a RÉSZVÉTELRŐL szól.
// Ha a bejelentkezett felhasználó MAGA is résztvevő, a lista fölött egy saját RSVP-vezérlő jelenik meg
// (ELSŐ személyű feliratok: „Ott leszek" / „Nem megyek" — a badge marad harmadik személyű, mert az MÁSOK
// állapotát is írja) → a `PUT /meetings/:id/rsvp` self-scope végpont (a token sub-jára). Az
// állítás optimista helyi visszajelzést ad, a lista-invalidálás a forrás-igazság. A résztvevő-halmaz
// SZERKESZTÉSE (add/remove) a form multiselectjeivel történik — ez a panel csak áttekint + a saját RSVP-t állítja.
//
// a soron megjelenő RSVP-jel a KÖZÖS badge-katalógusból jön (`domain-badge.tsx`, `rsvp` fogalom) — a helyi
// szín/ikon-térkép megszűnt.
import { type MeetingRowSchema } from '@atlas/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type z } from 'zod';

import { DomainBadge } from '@/components/shared/domain-badge';
import { PersonChip } from '@/components/shared/person-chip';
import { Button } from '@/components/ui/button';
import { useMeetingRsvp } from '@/features/meetings/use-meeting-rsvp';

type MeetingRow = z.infer<typeof MeetingRowSchema>;

export interface MeetingParticipantsPanelProps {
  meeting: MeetingRow;
  /** A bejelentkezett identity (token sub) — a saját RSVP-vezérlő csak akkor jelenik meg, ha ez résztvevő. */
  currentUserId: string | null;
}

export function MeetingParticipantsPanel({
  meeting,
  currentUserId,
}: MeetingParticipantsPanelProps): JSX.Element {
  const { t } = useTranslation();
  const participants = meeting.participants;

  // A saját résztvevő-sor (ha a bejelentkezett felhasználó meg van hívva) — a self-scope RSVP-vezérlőhöz.
  const mine = useMemo(
    () => (currentUserId ? (participants.find((p) => p.id === currentUserId) ?? null) : null),
    [participants, currentUserId],
  );
  // A saját RSVP a KÖZÖS hookból (ugyanaz a mutáció + optimista státusz hajtja az áttekintés widgetjét is).
  const {
    myStatus,
    isPending: rsvpPending,
    setRsvp,
  } = useMeetingRsvp(meeting.id, mine?.status ?? null);

  if (participants.length === 0) {
    return <p className="text-muted-foreground text-sm">{t('meetings.noParticipants')}</p>;
  }

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">{t('meetings.participants.title')}</p>

      {mine && (
        <div className="bg-sunken flex flex-wrap items-center gap-2 rounded-lg p-2.5">
          <span className="text-sm">{t('meetings.rsvp.myResponse')}</span>
          <div className="ml-auto flex gap-1.5">
            <Button
              type="button"
              size="sm"
              variant={myStatus === 'confirmed' ? 'brand' : 'outline'}
              disabled={rsvpPending}
              onClick={() => setRsvp('confirmed')}
            >
              {t('meetings.rsvp.actionConfirm')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={myStatus === 'declined' ? 'destructive' : 'outline'}
              disabled={rsvpPending}
              onClick={() => setRsvp('declined')}
            >
              {t('meetings.rsvp.actionDecline')}
            </Button>
          </div>
        </div>
      )}

      <ul className="space-y-1.5">
        {participants.map((p) => {
          // A saját soron az optimista helyi státusz látszik (a többi a szerver-válaszból).
          const status = p.id === currentUserId && myStatus ? myStatus : p.status;
          return (
            <li key={p.id} className="flex items-center gap-2.5">
              <PersonChip
                name={p.name ?? t('common.deletedUser')}
                givenName={p.givenName}
                familyName={p.familyName}
                avatarUrl={p.avatarUrl}
                accentColor={p.teamColor}
                size="sm"
                className="min-w-0 flex-1 text-sm"
              />
              <DomainBadge
                kind="rsvp"
                value={status}
                label={t(`meetings.rsvp.${status}`, { defaultValue: status })}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
